'use client';

import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { Box, Typography, Container, Button, TextField, IconButton, Tooltip, Snackbar, Alert, Grid, CircularProgress, Select, FormControl, InputLabel, List, ListItem, ListItemIcon, ListItemText, ListItemButton, useMediaQuery, useTheme, Fab, Drawer } from '@mui/material';
import dynamic from 'next/dynamic';
const MediaLibraryModal = dynamic<any>(() => import('@/components/content/MediaLibraryModal').then((m: any) => m.MediaLibraryModal || m.default || m), { ssr: false });
const ContentPreview = dynamic<any>(() => import('@/components/content/ContentPreview').then((m: any) => m.ContentPreview || m.default || m), { ssr: false });
// Schedule/Collaboration modals will be loaded on demand when opened to avoid preloading their chunks.
let ScheduleContentModal: any = null;
let CollaborationWorkflowModal: any = null;
const PerformanceForecastCard = dynamic<any>(() => import('@/components/content/PerformanceForecastCard').then((m: any) => m.PerformanceForecastCard || m.default || m), { ssr: false });
const ProductServiceSelectionModal = dynamic<any>(() => import('@/components/content/ProductServiceSelectionModal').then((m: any) => m.ProductServiceSelectionModal || m.default || m), { ssr: false });
const AdvancedAIPanel = dynamic<any>(() => import('@/components/content/AdvancedAIPanel').then((m: any) => m.default || m), { ssr: false });
const StickyActionBar = dynamic<any>(() => import('@/components/ui/StickyActionBar').then((m: any) => m.default || m), { ssr: false });

// AI Icons
import SpellcheckIcon from '@/letsgo/icons/Spellcheck';
import FormatQuoteIcon from '@/letsgo/icons/FormatQuote';
import SummarizeIcon from '@/letsgo/icons/Summarize';
import TagIcon from '@/letsgo/icons/Tag';
import EventNoteIcon from '@/letsgo/icons/EventNote'; // Icon for scheduling
import AutoAwesomeIcon from '@/letsgo/icons/AutoAwesome'; // General AI icon
import SearchIcon from '@/letsgo/icons/Search'; // SEO icon
import { LightbulbOutlined } from '@/lib/mui-optimized-imports';
import AttachMoneyIcon from '@/letsgo/icons/AttachMoney'; // Icon for monetization/products
import GroupAddIcon from '@/letsgo/icons/GroupAdd'; // Icon for collaboration
import AddPhotoAlternateIcon from '@/letsgo/icons/AddPhotoAlternate'; // Icon for image insertion (local optimized)
import {
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  StrikethroughS,
  FormatListNumbered,
  FormatListBulleted,
  InsertLink,
  Videocam,
  FormatClear,
  MoreVert,
} from '@mui/icons-material';
// Dynamically import EditorLoader
const EditorLoader = dynamic<any>(() => import('@/components/editor/EditorLoader').then((m: any) => m.default || m), { ssr: false });
// CreateToolbar moved into server-rendered toolbar to avoid duplicate toolbars.
import useDebounce from '@/hooks/useDebounce';

export default function CreatePage(props: any) {
  const initialDraftData: { title: string; content: string; key: string } | undefined = props?.initialDraftData;
  const [contentTitle, setContentTitle] = useState(initialDraftData?.title || '');
  const [editorContent, setEditorContent] = useState(initialDraftData?.content || '');
  const [isMediaModalOpen, setIsMediaModalOpen] = useState(false);
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
  const [isProductServiceModalOpen, setIsProductServiceModalOpen] = useState(false); // New state for product/service modal
  const [isCollaborationModalOpen, setIsCollaborationModalOpen] = useState(false); // New state for collaboration modal
  const [ScheduleComp, setScheduleComp] = useState<any>(null);
  const [CollabComp, setCollabComp] = useState<any>(null);
  const [attachedProductIds, setAttachedProductIds] = useState<string[]>([]); // New state for attached products
  const [collaborationStatus, setCollaborationStatus] = useState<string | null>(null); // New state for collaboration status
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);
  const [aiResponse, setAiResponse] = useState<string | null>(null);
  const [suggestedHashtags, setSuggestedHashtags] = useState<string[]>([]);
  const [suggestedKeywords, setSuggestedKeywords] = useState<string[]>([]);
  const [seoSuggestions, setSeoSuggestions] = useState<string[]>([]);
  const [selectedAiAction, setSelectedAiAction] = useState<string>('');
  const [isAdvancedAIDrawerOpen, setIsAdvancedAIDrawerOpen] = useState(false);
  const quillRef = useRef<any>(null);
  /** Prevents double-submit before React re-renders with aiLoading */
  const aiActionLockRef = useRef(false);
  const [isEditorActive, setIsEditorActive] = useState<boolean>(false);
  const [autosaveStatus, setAutosaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [insertSelectValue, setInsertSelectValue] = useState('');
  const [moreDrawerOpen, setMoreDrawerOpen] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const triggerToolbarAction = useCallback((action: string) => {
    document.getElementById('server-toolbar')?.querySelector<HTMLElement>(`[data-action="${action}"]`)?.click();
  }, []);

  // Load per-user draft from server on mount (Phase 2.3)
  const draftLoadedRef = useRef(false);
  useEffect(() => {
    if (draftLoadedRef.current) return;
    draftLoadedRef.current = true;
    fetch('/api/content/drafts?key=default')
      .then((r) => r.ok ? r.json() : null)
      .then((data: { draft?: { title?: string; content?: string } } | null) => {
        if (data?.draft?.title != null || data?.draft?.content != null) {
          setContentTitle(data.draft.title ?? '');
          setEditorContent(data.draft.content ?? '');
        }
      })
      .catch(() => {});
  }, []);

  // Prefill from Voice & Tone / AI Content (or other tools) via sessionStorage — see @/lib/docs/USE_IN_CREATE_PATTERN.md
  useEffect(() => {
    try {
      const raw = typeof window !== 'undefined' ? sessionStorage.getItem('createPagePrefill') : null;
      if (!raw) return;
      const prefill = JSON.parse(raw) as { title?: string; content?: string };
      sessionStorage.removeItem('createPagePrefill');
      if (prefill.title != null) setContentTitle(String(prefill.title));
      if (prefill.content != null) setEditorContent(String(prefill.content));
    } catch (_) {}
  }, []);

  // Debounce user input to reduce excessive processing and autosave frequency
  const debouncedTitle = useDebounce(contentTitle, 1000);
  const debouncedEditorContent = useDebounce(editorContent, 1500);

  useEffect(() => {
    // Skip autosave if both fields are empty
    if (!debouncedTitle && !debouncedEditorContent) return;

    setAutosaveStatus('saving');
    let aborted = false;
    (async () => {
      try {
        const res = await fetch('/api/content/drafts', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ key: 'default', title: debouncedTitle, content: debouncedEditorContent }),
        });
        if (aborted) return;
        if (!res.ok) throw new Error('Draft save failed');
        setAutosaveStatus('saved');
        const liveEl = document.getElementById('autosave-live');
        if (liveEl) {
          liveEl.textContent = 'Draft saved';
          setTimeout(() => { if (liveEl) liveEl.textContent = ''; }, 2000);
        }
        setTimeout(() => setAutosaveStatus('idle'), 2500);
      } catch (e) {
        if (!aborted) setAutosaveStatus('error');
        console.error('Autosave draft failed:', e);
      }
    })();

    return () => { aborted = true; };
  }, [debouncedTitle, debouncedEditorContent]);

  // Web Worker for offloading content analysis
  const contentWorkerRef = useRef<Worker | null>(null);

  useEffect(() => {
    try {
      if (typeof window !== 'undefined') {
        contentWorkerRef.current = new Worker('/workers/contentWorker.js');
        contentWorkerRef.current.onmessage = (ev) => {
          const data = ev.data as any;
          if (data?.keywords) setSuggestedKeywords(data.keywords);
          if (data?.wordCount) {
            // optionally use wordCount/readingTimeMinutes for UI hints
          }
        };
      }
    } catch (e) {
      console.error('Failed to initialize content worker', e);
    }
    return () => {
      if (contentWorkerRef.current) {
        contentWorkerRef.current.terminate();
        contentWorkerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (contentWorkerRef.current && debouncedEditorContent) {
      contentWorkerRef.current.postMessage({ text: debouncedEditorContent });
    }
  }, [debouncedEditorContent]);

  useEffect(() => {
    let mounted = true;
    if (isScheduleModalOpen && !ScheduleComp) {
      import('@/components/content/ScheduleContentModal').then((m:any) => {
        if (!mounted) return;
        const C = m.ScheduleContentModal || m.default || m;
        setScheduleComp(() => C);
      }).catch(() => {});
    }
    if (isCollaborationModalOpen && !CollabComp) {
      import('@/components/content/CollaborationWorkflowModal').then((m:any) => {
        if (!mounted) return;
        const C = m.CollaborationWorkflowModal || m.default || m;
        setCollabComp(() => C);
      }).catch(() => {});
    }
    return () => { mounted = false; };
  }, [isScheduleModalOpen, isCollaborationModalOpen, ScheduleComp, CollabComp]);

  const imageHandler = useCallback(() => {
    setIsMediaModalOpen(true);
  }, []);

  const insertMedia = useCallback((url: string, type: string) => {
    if (quillRef.current) {
      const editor = quillRef.current.getEditor();
      const range = editor.getSelection();
      if (range) {
        if (type.startsWith('image/')) {
          editor.insertEmbed(range.index, 'image', url);
        } else if (type.startsWith('video/')) {
          editor.insertEmbed(range.index, 'video', url);
        }
        editor.setSelection(range.index + 1); // Move cursor after inserted media
      }
    }
  }, []);

  const handleAiAction = useCallback(async (action: string, prompt?: string) => {
    if (aiActionLockRef.current) return;
    if (!contentTitle && !editorContent && action !== 'generate_draft') {
      setAiError("Please enter a title or some content to use AI assistance for this action.");
      return;
    }
    if (action === 'generate_draft' && !prompt) {
      setAiError("Please provide a prompt for content generation.");
      return;
    }

    aiActionLockRef.current = true;
    setAiLoading(true);
    setAiError(null);
    setAiResponse(null);
    setSuggestedHashtags([]);
    setSuggestedKeywords([]);
    setSeoSuggestions([]);

    try {
      const response = await fetch('/api/ai-assist', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action, content: editorContent, prompt }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "An unexpected AI error occurred.");
      }

      setAiResponse(data.aiResponse);
      if (data.suggestedHashtags) {
        setSuggestedHashtags(data.suggestedHashtags);
      }
      if (data.suggestedKeywords) {
        setSuggestedKeywords(data.suggestedKeywords);
      }
      if (data.seoSuggestions) {
        setSeoSuggestions(data.seoSuggestions);
      }

      // For actions that modify content (e.g., grammar check, tone adjust, generate, expand), update the editor
      if (['grammar_check', 'tone_adjust', 'generate_draft', 'expand_content'].includes(action)) {
        if (quillRef.current && data.aiResponse) {
          const editor = quillRef.current.getEditor();
          // For generation/expansion, we might replace or insert new content
          if (action === 'generate_draft') {
            editor.setText(data.aiResponse); // Replace all content
          } else if (action === 'expand_content') {
            const currentContent = editor.getText();
            editor.setText(`${currentContent}\n\n${data.aiResponse}`); // Append expanded content
          } else {
            // For grammar/tone, simple replacement
            editor.setText(data.aiResponse);
          }
          setEditorContent(editor.getHtml()); // Update state with new HTML
        }
      }

    } catch (e: any) {
      setAiError(e.message);
    } finally {
      setAiLoading(false);
      aiActionLockRef.current = false;
      setSelectedAiAction(''); // Reset dropdown after action
    }
  }, [contentTitle, editorContent]); // Added contentTitle to dependency array

  const handleScheduleContent = useCallback(async (scheduledDateTime: Date) => {
    if (!contentTitle || !editorContent) {
      setAiError("Please provide a title and content before scheduling.");
      throw new Error("Title and content are required.");
    }
    setAiError(null);
    const res = await fetch('/api/content/schedule', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        title: contentTitle,
        content: editorContent,
        scheduledFor: scheduledDateTime.toISOString(),
      }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(data?.error ?? `Schedule failed (${res.status})`);
    }
    setAiResponse(`Content "${contentTitle}" scheduled for ${scheduledDateTime.toLocaleDateString()} ${scheduledDateTime.toLocaleTimeString()}.`);
    setIsScheduleModalOpen(false);
  }, [contentTitle, editorContent]);

  const handleInitiateCollaboration = useCallback(async (request: any) => {
    setAiLoading(true);
    setAiError(null);
    setCollaborationStatus(null);
    try {
      const response = await fetch('/api/collaboration', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(request),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to initiate collaboration.");
      }
      setCollaborationStatus(data.message); // Display the success message
      setAiResponse(data.message);
      setIsCollaborationModalOpen(false);
    } catch (e: any) {
      setAiError(e.message);
    } finally {
      setAiLoading(false);
    }
  }, []);

  // REMOVED CustomToolbar COMPONENT - Now handled by CreateToolbar.tsx

  const quillModules = useMemo(() => ({
    // Use an empty modules object so Quill does not manage toolbar DOM (we wire controls ourselves)
  }), []);

  // Listen for toolbar-triggered custom events (e.g., insert-image) dispatched by the editor toolbar wiring
  useEffect(() => {
    const handler = () => setIsMediaModalOpen(true);
    document.addEventListener('quill-insert-image', handler);
    return () => document.removeEventListener('quill-insert-image', handler);
  }, []);

  const quillFormats = [
    'header',
    'bold', 'italic', 'underline', 'strike', 'blockquote',
    'list',
    'link', 'image', 'video'
  ];

  const handleSubmit = useCallback(async () => {
    if (!contentTitle || !editorContent) {
      setAiError("Please provide a title and content before saving.");
      return;
    }

    setAiLoading(true);
    setAiError(null);

    try {
      const contentResponse = await fetch('/api/content', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: contentTitle, content: editorContent }),
      });

      const contentData = await contentResponse.json();
      if (!contentResponse.ok) {
        throw new Error(contentData.error || 'Failed to save content.');
      }

      const contentId = contentData.id;
      if (typeof contentId !== 'string') {
        throw new Error('Invalid response: content ID missing.');
      }

      if (attachedProductIds.length > 0) {
        const monetizationResponse = await fetch('/api/monetization', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ contentId, productIds: attachedProductIds }),
        });

        const monetizationData = await monetizationResponse.json();
        if (!monetizationResponse.ok) {
          throw new Error(monetizationData.message || "Failed to attach products/services.");
        }
        setAiResponse(`Content saved and products attached: ${monetizationData.message}`);
      } else {
        setAiResponse("Content saved successfully!");
      }
    } catch (e: any) {
      setAiError(e.message);
    } finally {
      setAiLoading(false);
    }
  }, [contentTitle, editorContent, attachedProductIds]);

  // Phase 2 — keyboard save (Ctrl/Cmd+S): match sticky bar Save; prevent browser “Save page”.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (!(e.ctrlKey || e.metaKey)) return;
      if (e.key !== 's' && e.key !== 'S') return;
      e.preventDefault();
      void handleSubmit();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [handleSubmit]);

  const handleCloseSnackbar = useCallback(() => {
    setAiError(null);
    setAiResponse(null);
    setSuggestedKeywords([]);
    setSeoSuggestions([]);
  }, []);

  // Accessibility enhancements applied at runtime to improve axe results:
  // - Ensure icon-only toolbar buttons have aria-label
  // - Make hidden picker option containers unfocusable
  // - Add role="button" to spans with aria-labels when missing
  useEffect(() => {
    try {
      // Map common Quill button classes to labels
      const labelMap: Record<string,string> = {
        'ql-bold': 'Bold',
        'ql-italic': 'Italic',
        'ql-underline': 'Underline',
        'ql-strike': 'Strikethrough',
        'ql-blockquote': 'Blockquote',
        'ql-list': 'List',
        'ql-link': 'Insert link',
        'ql-image': 'Insert image',
        'ql-video': 'Insert video',
        'ql-clean': 'Remove formatting',
      };

      // Add aria-labels for known buttons
      Object.keys(labelMap).forEach((cls) => {
        document.querySelectorAll(`.ql-toolbar .${cls}`).forEach((el) => {
          if (el instanceof HTMLElement && !el.getAttribute('aria-label')) {
            el.setAttribute('aria-label', labelMap[cls]);
          }
        });
      });

      // Ensure hidden picker options are not focusable
      document.querySelectorAll('.ql-picker-options[aria-hidden="true"]').forEach((el) => {
        if (el instanceof HTMLElement) el.setAttribute('tabindex', '-1');
      });

      // Ensure spans with aria-label behave as buttons
      document.querySelectorAll('span[aria-label]').forEach((el) => {
        if (el instanceof HTMLElement && !el.getAttribute('role')) el.setAttribute('role', 'button');
      });
    } catch (e) {
      // swallow
    }
  }, []);

  return (
    <Container
      maxWidth="lg"
      id="main-content"
      sx={{
        mt: 4,
        mb: 4,
        pb: { xs: 14, md: 4 }, // safe-bottom padding to avoid BottomNav overlap
        // Scoped override to hide Quill picker triangles and tooltip artifacts
        '& .ql-tooltip.ql-hidden': {
          display: 'none !important',
        },
        '& .ql-tooltip': {
          backgroundColor: 'transparent !important',
          border: 'none !important',
          boxShadow: 'none !important',
        },
        '& .ql-picker .ql-picker-label svg, & .ql-picker .ql-picker-label svg polygon, & svg.polygon.ql-stroke, & svg polygon.ql-stroke': {
          display: 'none !important',
        },
        // Make toolbar wrap on small screens and allow formats to wrap
        '& .ql-toolbar': {
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1,
          alignItems: 'center',
        },
        '& .ql-formats': {
          display: 'flex',
          gap: 1,
          flexWrap: 'wrap',
          alignItems: 'center',
        },
        // Editor spacing and minimum height for mobile
        '& .ql-editor': {
          padding: '12px',
          minHeight: '40vh',
          maxHeight: isMobile ? 'calc(100vh - 350px)' : '70vh',
          overflowY: 'auto',
          boxSizing: 'border-box',
        },
        // Finger/thumb friendly: 44px min touch targets for all toolbar controls (a11y + mobile)
        '& .server-toolbar button, & .server-ql-header': {
          minHeight: 44,
          minWidth: 44,
        },
      }}
    >
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Create New Content
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          Start crafting your next piece of content here.
        </Typography>
      </Box>

      <Box sx={{ mb: 4 }}>
        <TextField
          label="Content Title"
          variant="outlined"
          fullWidth
          value={contentTitle}
          onChange={(e) => setContentTitle(e.target.value)}
          sx={{ mb: 3 }}
        />
          <Box sx={{ minHeight: isMobile ? 'calc(100vh - 350px)' : '400px', mb: 3, position: 'relative' }}> {/* Add position: 'relative' for FAB positioning */}
          {/* Toolbar: on mobile two rows (formatting wrap + Insert/Clear/More); on desktop single row with wrap */}
          <Box sx={{ width: '100%', overflow: 'visible', mb: 1 }} role="presentation">
            <div
              id="server-toolbar"
              className="server-toolbar"
              role="toolbar"
              aria-label="Editor formatting and actions"
              data-editor-toolbar-host
              style={{
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                flexWrap: isMobile ? 'nowrap' : 'wrap',
                justifyContent: 'flex-start',
                alignItems: 'center',
                gap: isMobile ? 8 : 16,
                width: '100%',
              }}
            >
              {/* Row 1: Header + text formatting only (Bold, Italic, Underline, Strike, Block quote) */}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', width: '100%' }}>
                <span className="ql-formats">
                  <select id="server-ql-header" className="server-ql-header" aria-label="Header level" aria-labelledby="ql-header-label">
                    <option value="1">Heading 1</option>
                    <option value="2">Heading 2</option>
                    <option value="3">Heading 3</option>
                    <option value="">Normal</option>
                  </select>
                </span>
                <span className="ql-formats" style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                  <Tooltip title="Bold">
                    <IconButton component="button" type="button" className="server-ql-btn ql-bold" data-action="bold" aria-label="Bold" sx={{ minWidth: 44, minHeight: 44 }}>
                      <FormatBold fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Italic">
                    <IconButton component="button" type="button" className="server-ql-btn ql-italic" data-action="italic" aria-label="Italic" sx={{ minWidth: 44, minHeight: 44 }}>
                      <FormatItalic fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Underline">
                    <IconButton component="button" type="button" className="server-ql-btn ql-underline" data-action="underline" aria-label="Underline" sx={{ minWidth: 44, minHeight: 44 }}>
                      <FormatUnderlined fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Strikethrough">
                    <IconButton component="button" type="button" className="server-ql-btn ql-strike" data-action="strike" aria-label="Strikethrough" sx={{ minWidth: 44, minHeight: 44 }}>
                      <StrikethroughS fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Block quote">
                    <IconButton component="button" type="button" className="server-ql-btn ql-blockquote" data-action="blockquote" aria-label="Block quote" sx={{ minWidth: 44, minHeight: 44 }}>
                      <FormatQuoteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </span>
              </Box>

              {/* Row 2: Ordered list, Bulleted list, Insert, Clear, More (all on same row on mobile) */}
              <Box sx={{ display: 'flex', flexWrap: isMobile ? 'nowrap' : 'wrap', gap: 1, alignItems: 'center', width: '100%', overflowX: isMobile ? 'auto' : 'visible', ...(isMobile && { WebkitOverflowScrolling: 'touch', pb: 0.5 }) }}>
                <span className="ql-formats" style={{ display: 'flex', gap: 4, alignItems: 'center', flexShrink: 0 }}>
                  <Tooltip title="Ordered list">
                    <IconButton component="button" type="button" className="server-ql-btn ql-list" data-action="list-ordered" aria-label="Ordered list" value="ordered" sx={{ minWidth: 44, minHeight: 44 }}>
                      <FormatListNumbered fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Bulleted list">
                    <IconButton component="button" type="button" className="server-ql-btn ql-list" data-action="list-bullet" aria-label="Bulleted list" value="bullet" sx={{ minWidth: 44, minHeight: 44 }}>
                      <FormatListBulleted fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </span>
                {/* Insert: link/image/video — hidden on mobile, replaced by Insert dropdown */}
                <span className="ql-formats" style={{ display: isMobile ? 'none' : 'flex', gap: 4, alignItems: 'center' }}>
                  <Tooltip title="Insert link">
                    <IconButton component="button" type="button" className="server-ql-btn ql-link" data-action="link" aria-label="Insert link" sx={{ minWidth: 44, minHeight: 44 }}>
                      <InsertLink fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Insert image">
                    <IconButton component="button" type="button" className="server-ql-btn ql-image" data-action="image" aria-label="Insert image" sx={{ minWidth: 44, minHeight: 44 }}>
                      <AddPhotoAlternateIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Insert video">
                    <IconButton component="button" type="button" className="server-ql-btn ql-video" data-action="video" aria-label="Insert video" sx={{ minWidth: 44, minHeight: 44 }}>
                      <Videocam fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </span>
                {isMobile && (
                  <span className="ql-formats" style={{ display: 'inline-flex', flexShrink: 0 }}>
                    <select
                      id="server-ql-insert"
                      className="server-ql-header"
                      aria-label="Insert media or link"
                      value={insertSelectValue}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v) triggerToolbarAction(v);
                        setInsertSelectValue('');
                      }}
                    >
                      <option value="">Insert</option>
                      <option value="link">Insert link</option>
                      <option value="image">Insert image</option>
                      <option value="video">Insert video</option>
                    </select>
                  </span>
                )}
                <span className="ql-formats" style={{ display: 'flex', gap: 4, alignItems: 'center', flexShrink: 0 }}>
                  <Tooltip title="Clear formatting">
                    <IconButton component="button" type="button" className="server-ql-btn ql-clean" data-action="clean" aria-label="Clear formatting" sx={{ minWidth: 44, minHeight: 44 }}>
                      <FormatClear fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </span>
                {/* AI & actions — hidden on mobile, replaced by More drawer */}
                <span className="ql-actions" style={{ marginLeft: isMobile ? 0 : 'auto', display: isMobile ? 'none' : 'flex', gap: 12, alignItems: 'center' }}>
                  <Tooltip title="Grammar Check">
                    <IconButton size="small" onClick={() => handleAiAction('grammar_check')} disabled={aiLoading} aria-label="Grammar Check" sx={{ minWidth: 44, minHeight: 44 }}>
                      <SpellcheckIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Summarize Content">
                    <IconButton size="small" onClick={() => handleAiAction('summarize')} disabled={aiLoading} aria-label="Summarize Content" sx={{ minWidth: 44, minHeight: 44 }}>
                      <SummarizeIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Adjust Tone (Professional)">
                    <IconButton size="small" onClick={() => handleAiAction('tone_adjust')} disabled={aiLoading} aria-label="Adjust Tone" sx={{ minWidth: 44, minHeight: 44 }}>
                      <FormatQuoteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Suggest Hashtags">
                    <IconButton size="small" onClick={() => handleAiAction('suggest_hashtags')} disabled={aiLoading} aria-label="Suggest Hashtags" sx={{ minWidth: 44, minHeight: 44 }}>
                      <TagIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Attach Products/Services">
                    <IconButton size="small" onClick={() => setIsProductServiceModalOpen(true)} disabled={aiLoading} aria-label="Attach Products or Services" sx={{ minWidth: 44, minHeight: 44 }}>
                      <AttachMoneyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Initiate Collaboration">
                    <IconButton size="small" onClick={() => setIsCollaborationModalOpen(true)} disabled={aiLoading} aria-label="Initiate Collaboration" sx={{ minWidth: 44, minHeight: 44 }}>
                      <GroupAddIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Advanced AI">
                    <IconButton size="small" onClick={() => setIsAdvancedAIDrawerOpen(true)} disabled={aiLoading} aria-label="Open Advanced AI panel" sx={{ minWidth: 44, minHeight: 44 }}>
                      <AutoAwesomeIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Schedule Content">
                    <IconButton size="small" onClick={() => setIsScheduleModalOpen(true)} aria-label="Schedule Content" sx={{ minWidth: 44, minHeight: 44 }}>
                      <EventNoteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </span>
                {isMobile && (
                  <>
                    <IconButton onClick={() => setMoreDrawerOpen(true)} aria-label="More actions" sx={{ minWidth: 44, minHeight: 44, ml: 'auto' }}>
                      <MoreVert />
                    </IconButton>
                    <Drawer
                      anchor="bottom"
                      open={moreDrawerOpen}
                      onClose={() => setMoreDrawerOpen(false)}
                      ModalProps={{
                        style: { zIndex: 1500 },
                        BackdropProps: { sx: { bgcolor: 'rgba(0,0,0,0.3)' } },
                      }}
                      PaperProps={{
                        sx: {
                          zIndex: 1500,
                          borderTopLeftRadius: 16,
                          borderTopRightRadius: 16,
                          maxHeight: '70vh',
                          pb: 2,
                        },
                      }}
                    >
                      <Box sx={{ pt: 1.5, pb: 0.5, px: 2, textAlign: 'center' }}>
                        <Box sx={{ width: 36, height: 4, borderRadius: 2, bgcolor: 'action.hover', mx: 'auto' }} aria-hidden />
                        <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>Editor actions</Typography>
                      </Box>
                      <List disablePadding>
                        <ListItemButton onClick={() => { handleAiAction('grammar_check'); setMoreDrawerOpen(false); }} sx={{ py: 1.5, minHeight: 48 }}>
                          <ListItemIcon sx={{ minWidth: 40 }}><SpellcheckIcon fontSize="small" /></ListItemIcon>
                          <ListItemText primary="Grammar Check" />
                        </ListItemButton>
                        <ListItemButton onClick={() => { handleAiAction('summarize'); setMoreDrawerOpen(false); }} sx={{ py: 1.5, minHeight: 48 }}>
                          <ListItemIcon sx={{ minWidth: 40 }}><SummarizeIcon fontSize="small" /></ListItemIcon>
                          <ListItemText primary="Summarize Content" />
                        </ListItemButton>
                        <ListItemButton onClick={() => { handleAiAction('tone_adjust'); setMoreDrawerOpen(false); }} sx={{ py: 1.5, minHeight: 48 }}>
                          <ListItemIcon sx={{ minWidth: 40 }}><FormatQuoteIcon fontSize="small" /></ListItemIcon>
                          <ListItemText primary="Adjust Tone" />
                        </ListItemButton>
                        <ListItemButton onClick={() => { handleAiAction('suggest_hashtags'); setMoreDrawerOpen(false); }} sx={{ py: 1.5, minHeight: 48 }}>
                          <ListItemIcon sx={{ minWidth: 40 }}><TagIcon fontSize="small" /></ListItemIcon>
                          <ListItemText primary="Suggest Hashtags" />
                        </ListItemButton>
                        <ListItemButton onClick={() => { setIsProductServiceModalOpen(true); setMoreDrawerOpen(false); }} sx={{ py: 1.5, minHeight: 48 }}>
                          <ListItemIcon sx={{ minWidth: 40 }}><AttachMoneyIcon fontSize="small" /></ListItemIcon>
                          <ListItemText primary="Attach Products/Services" />
                        </ListItemButton>
                        <ListItemButton onClick={() => { setIsCollaborationModalOpen(true); setMoreDrawerOpen(false); }} sx={{ py: 1.5, minHeight: 48 }}>
                          <ListItemIcon sx={{ minWidth: 40 }}><GroupAddIcon fontSize="small" /></ListItemIcon>
                          <ListItemText primary="Collaboration" />
                        </ListItemButton>
                        <ListItemButton onClick={() => { setIsAdvancedAIDrawerOpen(true); setMoreDrawerOpen(false); }} sx={{ py: 1.5, minHeight: 48 }}>
                          <ListItemIcon sx={{ minWidth: 40 }}><AutoAwesomeIcon fontSize="small" /></ListItemIcon>
                          <ListItemText primary="Advanced AI" />
                        </ListItemButton>
                        <ListItemButton onClick={() => { setIsScheduleModalOpen(true); setMoreDrawerOpen(false); }} sx={{ py: 1.5, minHeight: 48 }}>
                          <ListItemIcon sx={{ minWidth: 40 }}><EventNoteIcon fontSize="small" /></ListItemIcon>
                          <ListItemText primary="Schedule Content" />
                        </ListItemButton>
                      </List>
                    </Drawer>
                  </>
                )}
              </Box>
            </div>
          </Box>
          {isEditorActive ? (
            <EditorLoader
              ref={quillRef}
              value={editorContent}
              onChange={setEditorContent}
              modules={quillModules}
              formats={quillFormats}
              placeholder="Start writing your amazing content..."
              style={{ height: 'calc(100% - 42px)', borderTop: 'none' }} // Adjust height and remove top border
            />
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
              <Button variant="outlined" onClick={() => setIsEditorActive(true)} aria-label="Open editor">
                Open Editor
              </Button>
            </Box>
          )}
          {isMobile && (
            <Fab
              color="primary"
              aria-label="add image"
              sx={{
                position: 'absolute',
                bottom: theme.spacing(2),
                right: theme.spacing(2),
                zIndex: 1,
              }}
              onClick={() => {
                if (!isEditorActive) setIsEditorActive(true);
                imageHandler();
              }}
            >
              <AddPhotoAlternateIcon />
            </Fab>
          )}
        </Box>
        <StickyActionBar
          onSave={handleSubmit}
          onSaveAndSchedule={() => setIsScheduleModalOpen(true)}
          saving={aiLoading}
          autosaveStatus={autosaveStatus}
        />
      </Box>

      {aiLoading && <CircularProgress sx={{ mt: 2 }} />}
      {aiResponse && (
        <Alert severity="info" sx={{ mt: 2 }} onClose={handleCloseSnackbar}>
          AI Assist: {aiResponse}
        </Alert>
      )}
      {suggestedHashtags.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2">Suggested Hashtags:</Typography>
          <Grid container spacing={1} sx={{ mt: 1 }}>
            {suggestedHashtags.map((tag, index) => (
              <Grid item key={index}>
                <Button variant="outlined" size="small" onClick={() => setEditorContent(prev => prev + ` ${tag}`)}>
                  {tag}
                </Button>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}
      {aiError && (
        <Alert severity="error" sx={{ mt: 2 }} onClose={handleCloseSnackbar}>
          AI Error: {aiError}
        </Alert>
      )}

      {/* Attached Products/Services Section */}
      {attachedProductIds.length > 0 && (
        <Box sx={{ mt: 4, mb: 4 }}>
          <Typography variant="h5" component="h2" gutterBottom>Attached Products/Services</Typography>
          <List dense>
            {attachedProductIds.map((productId, index) => (
              <ListItem key={index} disablePadding>
                <ListItemIcon sx={{ minWidth: '30px' }}>
                  <AttachMoneyIcon fontSize="small" color="success" />
                </ListItemIcon>
                <ListItemText primary={productId} primaryTypographyProps={{ variant: 'body2' }} />
              </ListItem>
            ))}
          </List>
        </Box>
      )}

      {/* SEO Optimization Section */}
      {(suggestedKeywords.length > 0 || seoSuggestions.length > 0) && (
        <Box sx={{ mt: 4, mb: 4 }}>
          <Typography variant="h5" component="h2" gutterBottom>SEO Optimization</Typography>
          {suggestedKeywords.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>Suggested Keywords:</Typography>
              <Grid container spacing={1}>
                {suggestedKeywords.map((keyword, index) => (
                  <Grid item key={index}>
                    <Button variant="outlined" size="small" onClick={() => setEditorContent(prev => prev + ` ${keyword}`)}>
                      {keyword}
                    </Button>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}
          {seoSuggestions.length > 0 && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>Optimization Suggestions:</Typography>
              <List dense>
                {seoSuggestions.map((suggestion, index) => (
                  <ListItem key={index} disablePadding>
                    <ListItemIcon sx={{ minWidth: '30px' }}>
                      <LightbulbOutlined fontSize="small" color="warning" />
                    </ListItemIcon>
                    <ListItemText primary={suggestion} primaryTypographyProps={{ variant: 'body2' }} />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
        </Box>
      )}

      {/* Collaboration Status Section */}
      {collaborationStatus && (
        <Box sx={{ mt: 4, mb: 4 }}>
          <Typography variant="h5" component="h2" gutterBottom>Collaboration Status</Typography>
          <Alert severity="info">
            {collaborationStatus}
          </Alert>
        </Box>
      )}

      <ContentPreview
        title={contentTitle}
        contentHtml={editorContent}
      />

      <PerformanceForecastCard
        contentTitle={contentTitle}
        editorContent={editorContent}
      />

      <MediaLibraryModal
        open={isMediaModalOpen}
        onClose={() => setIsMediaModalOpen(false)}
        onSelectMedia={insertMedia}
      />

      {ScheduleComp && (
        <ScheduleComp
          open={isScheduleModalOpen}
          onClose={() => setIsScheduleModalOpen(false)}
          onSchedule={handleScheduleContent}
          contentTitle={contentTitle}
        />
      )}

      <ProductServiceSelectionModal
        open={isProductServiceModalOpen}
        onClose={() => setIsProductServiceModalOpen(false)}
        onSelectProducts={(ids: string[]) => setAttachedProductIds(ids)}
        initialSelectedProductIds={attachedProductIds}
      />

      {CollabComp && (
        <CollabComp
          open={isCollaborationModalOpen}
          onClose={() => setIsCollaborationModalOpen(false)}
          onInitiateCollaboration={handleInitiateCollaboration}
          contentTitle={contentTitle}
        />
      )}

      <Snackbar open={!!aiError || !!aiResponse} autoHideDuration={6000} onClose={handleCloseSnackbar}>
        <Alert onClose={handleCloseSnackbar} severity={aiError ? "error" : "info"} sx={{ width: '100%' }}>
          {aiError || aiResponse}
        </Alert>
      </Snackbar>
      {/* ARIA live region for autosave announcements (polite) */}
      <div id="autosave-live" aria-live="polite" aria-atomic="true" style={{position:'absolute',left:'-9999px'}} />
    </Container>
  );
}
